import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Play } from 'lucide-react';
import PageHero from '@/components/PageHero';
import { Reveal, SectionHeading } from '@/components/Reveal';
import { GALLERY_ITEMS } from '@/data/content';

const CATS = ['All', 'Campus', 'Classrooms', 'Events', 'Activities'];
const SPAN = { wide: 'md:col-span-2', tall: 'md:row-span-2', normal: '' };

const Gallery = () => {
  const [cat, setCat] = useState('All');
  const [lightbox, setLightbox] = useState(null);
  const items = GALLERY_ITEMS.filter((g) => cat === 'All' || g.cat === cat);

  return (
    <>
      <PageHero eyebrow="Gallery" title="Glimpses of life at Ideal." description="Classrooms, celebrations, competitions and campus moments — straight from Shahapur." />

      <section className="py-24 sm:py-32" data-testid="gallery-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap gap-3" role="tablist" aria-label="Gallery filters">
              {CATS.map((c) => (
                <button
                  key={c}
                  data-testid={`gallery-filter-${c.toLowerCase()}`}
                  onClick={() => setCat(c)}
                  role="tab"
                  aria-selected={cat === c}
                  className={`rounded-full px-5 py-2.5 text-sm font-bold transition-all ${cat === c ? 'bg-crimson text-white' : 'border border-navy/15 text-navy hover:border-crimson hover:text-crimson'}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>

          <motion.div layout className="mt-12 grid grid-cols-2 md:grid-cols-4 auto-rows-[170px] sm:auto-rows-[220px] gap-4">
            <AnimatePresence mode="popLayout">
              {items.map((g, i) => (
                <motion.button
                  layout
                  key={g.caption + g.src}
                  data-testid={`gallery-item-${i}`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  onClick={() => setLightbox(g)}
                  className={`group relative overflow-hidden rounded-lg text-left ${SPAN[g.span] || ''}`}
                  aria-label={`View ${g.caption}`}
                >
                  <img src={g.src} alt={g.caption} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <p className="absolute bottom-3 left-3 text-sm font-semibold text-cream opacity-0 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">{g.caption}</p>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <section className="bg-navy py-24 sm:py-28 noise-overlay" data-testid="video-gallery">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <SectionHeading dark eyebrow="Video Gallery" title="Watch our campus come alive" description="Annual function highlights, classroom moments and celebration videos are published on our social media channels — follow us to watch." />
          <Reveal delay={0.15}>
            <div className="group relative overflow-hidden rounded-lg">
              <img src="/images/campus-wide.jpg" alt="Campus video preview" className="w-full object-cover aspect-video" />
              <div className="absolute inset-0 bg-navy/40 flex items-center justify-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold text-navy transition-transform group-hover:scale-110">
                  <Play className="h-7 w-7 fill-navy ml-1" />
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-navy/95 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            role="dialog"
            aria-modal="true"
            aria-label={lightbox.caption}
          >
            <button data-testid="lightbox-close" aria-label="Close" className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-cream/20 text-cream hover:bg-cream/10 transition-colors">
              <X className="h-5 w-5" />
            </button>
            <motion.figure initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={lightbox.src} alt={lightbox.caption} className="w-full max-h-[75vh] rounded-lg object-contain" />
              <figcaption className="mt-4 text-center font-display text-lg italic text-cream">{lightbox.caption}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Gallery;
